import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { GetImages } from "../../utils/GetImages";
import { IntegrationsService } from "@propertelligent/client-api";

declare global {
  interface Window {
    google: any;
    initAutocomplete: () => void;
  }
}

const AddressAutocompleteRHF = () => {
  const searchInput = useRef<HTMLInputElement>(null);
  const [apiKey, setApiKey] = useState("");
  const [showClear, setShowClear] = useState(false);

  const { setValue, clearErrors, getValues } = useFormContext();

  useEffect(() => {
    IntegrationsService.postApiIntegrationsKeysGooglemaps()
      .then((res) => setApiKey(res.success))
      .catch((error) => console.error("API error:", error));
  }, []);

  const loadGoogleMaps = useCallback((apiKey: string) => {
    if (window.google?.maps) {
      initAutocomplete();
      return;
    }

    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      existingScript.addEventListener("load", initAutocomplete);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly&callback=initAutocomplete`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    window.initAutocomplete = initAutocomplete;
  }, []);


  const setAddressValues = (addressObj: any) => {
  const formValues = getValues(); // get current form shape

  const isNested =
    typeof formValues.address === "object" &&
    formValues.address !== null;

  if (isNested) {
    // Nested structure (address.streetAddress)
    setValue("address.streetAddress", addressObj.streetAddress);
    setValue("address.city", addressObj.city);
    setValue("address.state", addressObj.state);
    setValue("address.zip", addressObj.zip);
  } else {
    // Flat structure (address, city, state, zip)
    setValue("address", addressObj.streetAddress);
    setValue("city", addressObj.city);
    setValue("state", addressObj.state);
    setValue("zip", addressObj.zip);
  }

  clearErrors(["address", "city", "state", "zip"]);
};


  const initAutocomplete = useCallback(() => {
    if (!searchInput.current || !window.google) return;

    const autocompleteInstance = new window.google.maps.places.Autocomplete(searchInput.current, {
      fields: ["address_components", "geometry"],
      componentRestrictions: { country: "us" },
    });

    autocompleteInstance.addListener("place_changed", () => {
  const place = autocompleteInstance.getPlace();
  const address = extractAddress(place);

  setAddressValues(address); // ← simply call helper
  setShowClear(true);
});

  }, [setValue, clearErrors]);

  const extractAddress = (place: any) => {
    const address = {
      streetAddress: "",
      city: "",
      state: "",
      zip: "",
    };

    place?.address_components?.forEach((component: any) => {
      const types = component.types;
      const value = component.long_name;

      if (types.includes("street_number")) address.streetAddress = value;
      if (types.includes("route")) address.streetAddress += " " + value;
      if (types.includes("locality")) address.city = value;
      if (types.includes("administrative_area_level_1"))  address.state = component.short_name;
      if (types.includes("postal_code")) address.zip = value;
    });

    return address;
  };

  const clearAddress = () => {
  const formValues = getValues();
  const isNested =
    typeof formValues.address === "object" &&
    formValues.address !== null;

  if (isNested) {
    setValue("address.streetAddress", "");
    setValue("address.city", "");
    setValue("address.state", "");
    setValue("address.zip", "");
  } else {
    setValue("address", "");
    setValue("city", "");
    setValue("state", "");
    setValue("zip", "");
  }

  setShowClear(false);
};


  const handleClear = () => {
    if (searchInput.current) searchInput.current.value = "";
    clearAddress();
    setShowClear(false);
  };

  useEffect(() => {
    if (apiKey) loadGoogleMaps(apiKey);
  }, [apiKey, loadGoogleMaps]);

  return (
    <div className="AddressAutocomplete form-group">
        <label className="fw-600">
        Search Address
      </label>
      <div className="search position-relative form-control">
        <img src={GetImages.Search} alt="Search" className="iconstyle24" />
        <input
          className="sub_heading fw-normal grey_subtext1"
          ref={searchInput}
          type="text"
          placeholder="Search Address"
          onInput={(e) => setShowClear((e.target as HTMLInputElement).value.length > 0)}
        />

        {showClear && (
          <button
            type="button"
            onClick={handleClear}
            className="btn btn-clear position-absolute end-0 top-50 translate-middle-y me-2"
            style={{ background: "none", border: "none", padding: 0 }}
          >
            <img src={GetImages.closeIconBlack} className="iconstyle24" alt="Clear" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AddressAutocompleteRHF;
